import { useRef } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AlertDialog, AlertDialogBody, AlertDialogCloseButton, AlertDialogContent,
  AlertDialogFooter, AlertDialogHeader, AlertDialogOverlay, Button, Text,
} from '@chakra-ui/react';

type ConfirmDialogProps = {
  isOpen: boolean;
  title: string;
  message: ReactNode;
  warning?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  confirmColorScheme?: string;
  onConfirm: () => void;
  onClose: () => void;
};

/**
 * 破壊的操作（削除・取消）向けの共通確認ダイアログ。
 * 「取消確認」モーダルと同じ構成：タイトル＋✕、本文＋注意書き、実行(primary)／キャンセル。
 * キャンセル側にフォーカスを当てる（誤operationで実行されないよう least-destructive-ref）。
 */
export default function ConfirmDialog({
  isOpen, title, message, warning, confirmLabel, cancelLabel,
  confirmColorScheme = 'blue', onConfirm, onClose,
}: ConfirmDialogProps) {
  const { t } = useTranslation();
  const cancelRef = useRef<HTMLButtonElement>(null);
  const resolvedConfirmLabel = confirmLabel ?? t('common.button.execute');
  const resolvedCancelLabel = cancelLabel ?? t('common.button.cancel');

  return (
    <AlertDialog isOpen={isOpen} leastDestructiveRef={cancelRef} onClose={onClose} isCentered>
      <AlertDialogOverlay>
        <AlertDialogContent mx={4}>
          <AlertDialogHeader fontSize="lg" fontWeight="bold">{title}</AlertDialogHeader>
          <AlertDialogCloseButton />
          <AlertDialogBody>
            <Text>{message}</Text>
            {warning && <Text fontSize="sm" color="gray.500" mt={2}>{warning}</Text>}
          </AlertDialogBody>
          <AlertDialogFooter>
            <Button colorScheme={confirmColorScheme} onClick={() => { onConfirm(); onClose(); }}>
              {resolvedConfirmLabel}
            </Button>
            <Button ref={cancelRef} variant="outline" ml={3} onClick={onClose}>
              {resolvedCancelLabel}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );
}
